import { graphqlQuery } from '@/lib/api/graphql';
import type { GraphQLCursorConnection } from '@/lib/graphql-pagination';
import type { CaseAuditLog } from '@/types/collections';
import { useCursorPagination } from './useCursorPagination';

// --- GraphQL types ---

interface CaseAuditLogGraphQLNode {
  auditLogId: number;
  caseId: number;
  caseNumber: string | null;
  actionTypeName: string | null;
  description: string;
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  transactionId: number | null;
  transactionNumber: string | null;
  transactionAmount: number | null;
  transactionCurrencyCode: string | null;
  transactionDate: string | null;
  createdByUserName: string | null;
  createdAt: string;
}

interface CaseAuditLogListGraphQLResponse {
  collectionCases: {
    caseAuditLogList: GraphQLCursorConnection<CaseAuditLogGraphQLNode>;
  };
}

// --- GraphQL query ---

const CASE_AUDIT_LOG_LIST_QUERY = `
  query CaseAuditLogList($first: Int, $after: String, $sort: [CaseAuditLogListSortSortByInput!], $filter: CaseAuditLogListFilterInput) {
    collectionCases {
      caseAuditLogList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            auditLogId
            caseId
            caseNumber
            actionTypeName
            description
            fieldName
            oldValue
            newValue
            transactionId
            transactionNumber
            transactionAmount
            transactionCurrencyCode
            transactionDate
            createdByUserName
            createdAt
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`;

const AUDIT_LOG_PAGE_SIZE = 25;

// --- Mapping ---

const actionTypeToActionMap: Record<string, CaseAuditLog['action']> = {
  status_change: 'status_change',
  transaction_added: 'transaction_added',
  transaction_removed: 'transaction_removed',
  priority_change: 'priority_change',
  assignment_change: 'assignment_change',
  field_update: 'field_update',
  note_added: 'note_added',
  case_created: 'case_created',
  stage_increment: 'stage_increment',
  automated_action_executed: 'automated_action_executed',
};

function mapGraphQLNodeToCaseAuditLog(node: CaseAuditLogGraphQLNode): CaseAuditLog {
  const actionName = (node.actionTypeName ?? '').toLowerCase().replace(/\s+/g, '_');
  return {
    id: String(node.auditLogId),
    caseId: String(node.caseId),
    action: actionTypeToActionMap[actionName] || 'field_update',
    description: node.description,
    fieldName: node.fieldName || undefined,
    oldValue: node.oldValue,
    newValue: node.newValue,
    transactionId: node.transactionId ? String(node.transactionId) : undefined,
    transactionNumber: node.transactionNumber || undefined,
    transactionAmount: node.transactionAmount || undefined,
    transactionCurrencyCode: node.transactionCurrencyCode || undefined,
    transactionDate: node.transactionDate ? new Date(node.transactionDate) : undefined,
    createdBy: node.createdByUserName || 'System',
    createdAt: new Date(node.createdAt),
  };
}

// --- Mock data ---

function buildMockAuditLogs(caseId: string, caseAmount: number, customerEmail: string): CaseAuditLog[] {
  return [
    {
      id: 'audit-10',
      caseId,
      action: 'note_added',
      description: 'Follow-up note added',
      createdBy: 'Sarah Johnson',
      createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'audit-9',
      caseId,
      action: 'status_change',
      description: 'Case status changed',
      fieldName: 'status',
      oldValue: 'Active',
      newValue: 'Promised',
      createdBy: 'Sarah Johnson',
      createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'audit-8',
      caseId,
      action: 'transaction_added',
      description: 'Transaction linked to case',
      transactionNumber: 'INV-2024-002',
      transactionAmount: caseAmount * 0.35,
      transactionDate: new Date(2024, 1, 1),
      createdBy: 'System',
      createdAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'audit-7b',
      caseId,
      action: 'transaction_removed',
      description: 'Transaction removed from case',
      transactionId: '45',
      transactionNumber: 'INV-2024-005',
      transactionAmount: caseAmount * 0.15,
      transactionDate: new Date(2024, 0, 20),
      createdBy: 'Sarah Johnson',
      createdAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'audit-7',
      caseId,
      action: 'assignment_change',
      description: 'Case reassigned',
      fieldName: 'assignedTo',
      oldValue: 'John Smith',
      newValue: 'Sarah Johnson',
      createdBy: 'System',
      createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'audit-6',
      caseId,
      action: 'field_update',
      description: `Customer email updated to ${customerEmail}`,
      fieldName: 'customerEmail',
      oldValue: 'old@example.com',
      newValue: customerEmail,
      createdBy: 'System',
      createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'audit-5',
      caseId,
      action: 'note_added',
      description: 'Note added to case',
      createdBy: 'Mike Chen',
      createdAt: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'audit-4',
      caseId,
      action: 'priority_change',
      description: 'Case priority changed',
      fieldName: 'priority',
      oldValue: 'Medium',
      newValue: 'High',
      createdBy: 'Sarah Johnson',
      createdAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'audit-3',
      caseId,
      action: 'transaction_added',
      description: 'Transaction linked to case',
      transactionNumber: 'INV-2024-001',
      transactionAmount: caseAmount * 0.4,
      transactionDate: new Date(2024, 0, 15),
      createdBy: 'System',
      createdAt: new Date(Date.now() - 27 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'audit-2',
      caseId,
      action: 'status_change',
      description: 'Case status changed',
      fieldName: 'status',
      oldValue: 'New',
      newValue: 'Active',
      createdBy: 'Sarah Johnson',
      createdAt: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'audit-1',
      caseId,
      action: 'case_created',
      description: 'Collection case created',
      createdBy: 'System',
      createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    },
  ];
}

// --- Hook ---

export interface CaseAuditLogsGraphQLResult {
  auditLogs: CaseAuditLog[];
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  reset: () => void;
  refetch: () => void;
}

export function useCaseAuditLogsGraphQL(
  caseId: string | undefined,
  caseAmount: number,
  customerEmail: string,
): CaseAuditLogsGraphQLResult {
  const result = useCursorPagination<CaseAuditLogGraphQLNode, CaseAuditLog>(
    {
      pageSize: AUDIT_LOG_PAGE_SIZE,
      buildMockData: () => (caseId ? buildMockAuditLogs(caseId, caseAmount, customerEmail) : []),
      executeQuery: async (apiConfig, afterCursor, signal) => {
        if (!caseId) {
          return { edges: [], pageInfo: { endCursor: null, hasNextPage: false } };
        }
        const variables: Record<string, unknown> = {
          first: AUDIT_LOG_PAGE_SIZE,
          sort: [{ field: 'CreatedAt', order: 'Descending' }],
          filter: { caseId: { eq: Number(caseId) } },
        };
        if (afterCursor) {
          variables.after = afterCursor;
        }
        const response = await graphqlQuery<CaseAuditLogListGraphQLResponse>(
          apiConfig,
          CASE_AUDIT_LOG_LIST_QUERY,
          variables,
          signal,
        );
        return response.collectionCases.caseAuditLogList;
      },
      mapNode: mapGraphQLNodeToCaseAuditLog,
      postProcess: (items) => [...items].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
    },
    [caseId, caseAmount, customerEmail],
  );

  return {
    auditLogs: result.items,
    isLoading: result.isLoading,
    isFetchingMore: result.isFetchingMore,
    hasMore: result.hasMore,
    loadMore: result.loadMore,
    reset: result.reset,
    refetch: result.refetch,
  };
}
